import { m } from "#/paraglide/messages";
import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@beweegbaas/ui";
import { createFileRoute, Link, Outlet, stripSearchParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { LessonsSubnavigation } from "#/components/lessons-subnavigation";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { LessonsOverviewCardLayout } from "#/modules/school.lessen/components/lessons-overview-card-layout";
import { LessonsOverviewTableLayout } from "#/modules/school.lessen/components/lessons-overview-table-layout";
import { getLessonsOverview } from "#/modules/school.lessen/lessons.functions";
import { getLessonsOverviewCardTab } from "#/modules/school.lessen/params.lessen-overview";
import {
  LESSONS_SEARCH_DEFAULTS,
  lessonsSearchSchema,
} from "#/modules/school.lessen/schema.search";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/lessons/")({
  validateSearch: lessonsSearchSchema,
  // Keeps the default filter out of the URL.
  search: { middlewares: [stripSearchParams(LESSONS_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the loader/server-fn uses — never the whole search object.
  loaderDeps: ({ search }) => ({ date: search.date, dateFilterValue: search.dateFilterValue }),
  loader: ({ deps }) => getLessonsOverview({ data: deps }),
  component: LessonsPage,
});

function LessonsPage() {
  const { locale, school } = Route.useParams();
  const params = { locale, school };
  const { todayLessons, tomorrowLessons, futureLessons, hasPastLessons, tableLessons } =
    Route.useLoaderData();
  const { date, dateFilterValue, highlightLesson } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [animatingLesson, setAnimatingLesson] = useState<string | null>(null);

  const upcomingCount = todayLessons.length + tomorrowLessons.length + futureLessons.length;
  const showEmptyState = upcomingCount === 0 && !hasPastLessons;

  const currentTabValue = `${dateFilterValue}:${date}`;
  const cardTab = getLessonsOverviewCardTab({ date, dateFilterValue }) ?? "today";

  useEffect(() => {
    if (!highlightLesson) return;
    setAnimatingLesson(highlightLesson);
    const timer = setTimeout(() => {
      setAnimatingLesson(null);
      // One-time highlight consumed — strip it from the URL so it doesn't persist across reloads
      // or re-renders.
      void navigate({
        search: (prev) => ({ ...prev, highlightLesson: null }),
        replace: true,
        resetScroll: false,
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [highlightLesson, navigate]);

  return (
    <Page>
      {!showEmptyState ? (
        <>
          <Page.Header>
            <Page.Title>Lessen</Page.Title>

            <Page.Header.Actions>
              <Page.Header.MainAction>
                <Button
                  size="xl"
                  nativeButton={false}
                  render={
                    <Link
                      from={Route.fullPath}
                      to="/$locale/{-$schoolyear}/$school/start/les-flow/1"
                      params={(prev: Record<string, string>) => prev}
                    />
                  }
                >
                  Plan nog een les
                  <IconMaterialSymbolsDoubleArrowRounded />
                </Button>
              </Page.Header.MainAction>
            </Page.Header.Actions>
          </Page.Header>
          <LessonsSubnavigation currentTabValue={currentTabValue} />
        </>
      ) : (
        <Page.MobileHeader />
      )}
      {/* Direct child of the Page flex column, like the rapporten empty state: `flex-1` on
          Empty stretches it over the remaining page height so it centers vertically. */}
      {showEmptyState ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{m["lessen.geen_lessen_gevonden"]()}</EmptyTitle>
            <EmptyDescription>
              Er zijn nog geen afgeronde of geplande lessen beschikbaar.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              variant="default"
              size="xl"
              nativeButton={false}
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/start/les-flow/1"
                  params={(prev: Record<string, string>) => prev}
                />
              }
            >
              Start een les
              <IconMaterialSymbolsDoubleArrowRounded />
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <Page.Body>
          <PendingContent>
            {tableLessons !== null ? (
              <LessonsOverviewTableLayout lessons={tableLessons} params={params} />
            ) : (
              <LessonsOverviewCardLayout
                params={params}
                cardTab={cardTab}
                todayLessons={todayLessons}
                tomorrowLessons={tomorrowLessons}
                futureLessons={futureLessons}
                animatingLesson={animatingLesson}
              />
            )}
          </PendingContent>
        </Page.Body>
      )}
      <Outlet />
    </Page>
  );
}
